import React, { useState, useEffect } from 'react';
import { Container, Typography, Card, CardContent, CardMedia, Button, Box, Divider, CircularProgress } from '@mui/material';
import Grid from '@mui/material/Grid'; // or '@mui/material/Unstable_Grid2'
import { GridSize } from '@mui/material/Grid';
import { getCurrentStage, getParticipants, voteParticipant, listSessions } from './api';

const roles = ['Table Topic', 'Speaker', 'Evaluator'];

interface Participant {
  _id: string;
  name: string;
  role?: string;
  speechTitle?: string;
  imageUrl?: string;
  voters?: string[];
}

const shuffleParticipants = (items: Participant[]): Participant[] => {
  const shuffled = [...items];

  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const randomIndex = Math.floor(Math.random() * (index + 1));
    [shuffled[index], shuffled[randomIndex]] = [shuffled[randomIndex], shuffled[index]];
  }

  return shuffled;
};

interface VotingPageProps {
  session: string;
}

const VotingPage: React.FC<VotingPageProps> = ({ session }) => {
  useEffect(() => {
    document.title = 'ACE The Place Toastmasters Club';
  }, []);
  const [currentRole, setCurrentRole] = useState('Table Topic');
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [voted, setVoted] = useState(false);
  const [voteResponse, setVoteResponse] = useState<any>(null);
  const [hasVoted, setHasVoted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionNotFound, setSessionNotFound] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Generate or retrieve a unique voterId for this session
  const [voterId] = useState(() => {
    let id = localStorage.getItem('voterId');
    if (!id) {
      id = Math.random().toString(36).substr(2, 9);
      localStorage.setItem('voterId', id);
    }
    return id;
  });

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setSessionNotFound(false);
      setErrorMessage('');
      
      try {
        // First check if session exists in the session management system
        const sessionsResponse = await listSessions();
        const sessionExists = sessionsResponse.some((s: any) => s.session === session);
        
        if (!sessionExists) {
          setSessionNotFound(true);
          setErrorMessage(`Session "${session}" not found in the system`);
          return;
        }
        
        // Get current stage
        const res = await getCurrentStage(session);
        const role = res.currentRole || res.data?.currentRole || 'Table Topic';
        setCurrentRole(role);
        
        // Get participants for the current role
        const res2 = await getParticipants(session, role);
        const participantsData: Participant[] = res2.data?.data || res2.data || [];
        setParticipants(shuffleParticipants(participantsData));

        // Check if this voter has already voted for this role
        const alreadyVoted = participantsData.some((p: any) => p.voters && p.voters.includes(voterId));
        setHasVoted(alreadyVoted);
        
        if (alreadyVoted) {
          const votedId = localStorage.getItem(`${session}_${role}_votedId`);
          const votedParticipant = participantsData.find((p: any) => p._id === votedId);
          if (votedParticipant) setVoteResponse(votedParticipant);
        }
        
        setVoted(false);
        setSelectedId(null);
        if (!alreadyVoted) {
          setVoteResponse(null);
        }
      } catch (error: any) {
        console.error('Error fetching data:', error);
        
        // Check for specific error codes or messages that indicate session doesn't exist
        if (error.response?.status === 404 || 
            error.message?.includes('not found') || 
            error.message?.includes('does not exist') ||
            error.response?.data?.error?.includes('not found') ||
            error.response?.data?.error?.includes('does not exist')) {
          setSessionNotFound(true);
          setErrorMessage(error.response?.data?.error || error.message || 'Session not found');
        } else {
          setErrorMessage('Failed to load session data. Please try again.');
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [session, voterId]);

  const handleVote = async (id: string) => {
    const res = await voteParticipant(session, id, voterId);
    setVoted(true);
    setVoteResponse(res);
    setHasVoted(true);
    localStorage.setItem(`${session}_${currentRole}_votedId`, id);
  };

  return (
    <>
      <style>
        {`
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes slideUpScale {
            from {
              opacity: 0;
              transform: translateX(-50%) translateY(100px) scale(0.9);
            }
            to {
              opacity: 1;
              transform: translateX(-50%) translateY(0) scale(1);
            }
          }
          
          @keyframes colorNudge {
            0% {
              background-color: #1976d2;
              box-shadow: 0 0 0 0 rgba(25, 118, 210, 0.7);
            }
            50% {
              background-color: #0d47a1;
              box-shadow: 0 0 0 10px rgba(25, 118, 210, 0);
            }
            100% {
              background-color: #1976d2;
              box-shadow: 0 0 0 0 rgba(25, 118, 210, 0.7);
            }
          }

          .tiled-bg {
            background-image: url('/tile_background.png');
            background-repeat: repeat;
            background-size: auto;
          }

          /* Remove Samsung/Android touch highlights */
          * {
            -webkit-tap-highlight-color: transparent;
            -webkit-touch-callout: none;
            -webkit-user-select: none;
            -khtml-user-select: none;
            -moz-user-select: none;
            -ms-user-select: none;
            user-select: none;
          }
        `}
      </style>
      {/* Loading Screen */}
      {isLoading && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(119, 36, 50, 1)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            color: 'white'
          }}
        >
          <CircularProgress 
            size={60} 
            sx={{ 
              color: '#1976d2',
              mb: 3 
            }} 
          />
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 500,
              fontSize: { xs: '1.1rem', sm: '1.3rem' },
              textAlign: 'center',
              px: 2
            }}
          >
            Loading voting session...
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              mt: 1,
              fontSize: { xs: '0.9rem', sm: '1rem' },
              textAlign: 'center',
              opacity: 0.8,
              px: 2
            }}
          >
            Please wait while we fetch the current stage and participants
          </Typography>
        </Box>
      )}

      {/* Session Not Found Screen */}
      {sessionNotFound && !isLoading && (
        <Container disableGutters sx={{ mt: { xs: 2, sm: 4 }, minHeight: '100vh', position: 'relative' }}>
          <Box sx={{ mb: { xs: 2, sm: 3 }, pt: { xs: 12, sm: 12 }, position: 'relative', width: '100%', overflow: 'hidden' }}>
            <Typography variant="h4" align="left" sx={{ position: 'relative', zIndex: 1, fontWeight: 600, color: '#FFF', letterSpacing: 1, fontSize: { xs: '1.3rem', sm: '1.8rem' }, fontFamily: 'Montserrat, Arial, sans-serif', background: 'transparent', px: { xs: 2, sm: 4 }  }}>
              Session Not Found
            </Typography>
          </Box>
          <Container disableGutters maxWidth={false} sx={{
            width: '100%',
            pt: { xs: 4, sm: 6 },
            pb: { xs: 16, sm: 0 },
            boxShadow: 0,
            borderTop: '6px solid #772432',
            minHeight: 'calc(100vh - 120px)',
            mb: 0,
            bgcolor: '#F3EEE0',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1
          }}>
            <Box sx={{ 
              textAlign: 'center', 
              maxWidth: 500, 
              mx: 'auto',
              p: { xs: 3, sm: 4 },
              bgcolor: '#fff',
              borderRadius: 3,
              boxShadow: 3,
              border: '2px solid #f44336'
            }}>
              <Typography 
                variant="h1" 
                sx={{ 
                  fontSize: { xs: '4rem', sm: '6rem' },
                  color: '#f44336',
                  mb: 2,
                  fontWeight: 300
                }}
              >
                ❌
              </Typography>
              <Typography 
                variant="h5" 
                sx={{ 
                  fontWeight: 600,
                  color: '#f44336',
                  mb: 2,
                  fontSize: { xs: '1.3rem', sm: '1.5rem' }
                }}
              >
                Session Not Found
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  color: '#666',
                  mb: 3,
                  fontSize: { xs: '1rem', sm: '1.1rem' },
                  lineHeight: 1.6
                }}
              >
                The voting session "<strong>{session}</strong>" does not exist or has been removed.
              </Typography>
              {errorMessage && (
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#888',
                    mb: 3,
                    fontSize: '0.9rem',
                    fontStyle: 'italic',
                    p: 2,
                    bgcolor: '#f5f5f5',
                    borderRadius: 1,
                    border: '1px solid #e0e0e0'
                  }}
                >
                  Error details: {errorMessage}
                </Typography>
              )}
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, justifyContent: 'center' }}>
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={() => window.location.href = '/'}
                  sx={{ 
                    px: 3, 
                    py: 1.5, 
                    borderRadius: 2,
                    fontSize: '1rem',
                    fontWeight: 600
                  }}
                >
                  🏠 Go to Home
                </Button>
                <Button 
                  variant="outlined" 
                  color="secondary" 
                  onClick={() => window.location.reload()}
                  sx={{ 
                    px: 3, 
                    py: 1.5, 
                    borderRadius: 2,
                    fontSize: '1rem',
                    fontWeight: 600
                  }}
                >
                  🔄 Try Again
                </Button>
              </Box>
            </Box>
          </Container>
        </Container>
      )}

      {/* Main Voting Content */}
      {!isLoading && !sessionNotFound && (
      <Container  disableGutters maxWidth={false} className="tiled-bg" sx={{
        width: '100%',
        pt: { xs: 2, sm: 4 },
        pb: { xs: 16, sm: 0 },
        boxShadow: 0,
        minHeight: 'calc(100vh)',
        mb: 0,
        bgcolor: '#f8f6f1ff',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        zIndex: 1
      }}>
        
        <Typography variant="h4" align="left" sx={{ animation: `fadeInUp 1.2s ease-out both`, position: 'relative', zIndex: 1, fontWeight: 250, color: '#111', letterSpacing: 1, fontSize: { xs: '2.8rem', sm: '2.8rem' }, fontFamily: 'Montserrat, Arial, sans-serif', background: 'transparent', px: { xs: 3, sm: 4 }, pt: { xs: 2, sm: 4 },pb: { xs: 2, sm: 4 }  }}>
          Best <br/>{currentRole}
        </Typography>
        {!voted && !hasVoted ? (
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2} justifyContent="center" alignItems="center" columnSpacing={0}>
              {participants.map((p: Participant, index: number): React.ReactNode => (
                <Grid key={p._id} sx={{
                  animation: `fadeInUp 1.2s ease-out ${(index+1) * 0.3}s both`
                }}>
                  <Card
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      py: 0,
                      px: 0,
                      borderRadius: 3,
                      width: 'fit-content',
                      height: p.speechTitle? 210: 165,
                      maxWidth: 400,
                      border: selectedId === p._id ? '1px solid #149ae2ff' : '1px solid #bbbbbb',
                      background: '#f8f8f8ff',
                      transition: 'border 0.5s, box-shadow 0.5s',
                      cursor: voted || hasVoted ? 'default' : 'pointer',
                      '&:hover': !voted && !hasVoted ? { border: '1px solid #149ae2ff' } : {},
                      mx: 0.8,
                      overflow: 'hidden',
                      boxShadow: selectedId === p._id ? '0px 0px 5px 3px #149ae24d' : 'none'
                    }}
                    onClick={() => !voted && !hasVoted && setSelectedId(p._id)}
                  >
                    {p.imageUrl && (
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center',
                        py: 0.7,
                        px: 0.7
                      }}>
                        <CardMedia
                          component="img"
                          image={`${process.env.REACT_APP_API_URL}/api/${session}/image/${p.imageUrl.split('/').pop()}`}
                          alt={p.name}
                          sx={{
                            objectFit: 'cover',
                            width: 150,
                            height: 150,
                            borderRadius: 2,
                            background: '#f8f8f8ff',
                            transition: 'all 0.2s',
                            display: 'block'
                          }}
                        />
                      </Box>
                    )}

                      <Box sx={{ position: 'absolute', top: 132, left: 8, width:'fit-content', textAlign: 'left', borderTopRightRadius:15, borderBottomLeftRadius:5,  px:1.5, py:0.4, backgroundColor: '#f8f8f8ff'  }}>
                        <Typography variant="h6" gutterBottom sx={{ fontFamily:'Montserrat, Arial', fontWeight: 500, fontSize: { xs: '0.9rem' }, color: selectedId === p._id ? '#006094' : '#333', mb: 0 }}>{p.name}</Typography>
                        
                      </Box>
                      {p.speechTitle && (
                        <Box sx={{width:'150px'}}>
                          <Typography variant="body2" sx={{ fontFamily:'Montserrat, Arial', fontWeight: 300, fontSize: { xs: '0.7rem' }, color: selectedId === p._id ? '#0077c2' : '#555', mt: 0.5, mx:1.5, fontStyle: 'italic' }}>
                            "{p.speechTitle}"
                          </Typography>
                        </Box>
                      )}
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        ) : (
          <Box sx={{ animation: 'fadeInUp 1.2s ease-out both', mt: 2, mx: { xs: 2, sm: 'auto' }, maxWidth: 440, p: 3, textAlign: 'center', borderRadius: 3, background: '#fff'}}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: voted ? '#2e7d32' : '#772432', mb: 2 }}>
              {voted ? 'Thank you for voting!' : 'You have already voted for this stage.'}
            </Typography>
            {voteResponse && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                  {voted ? 'Your vote has been recorded for:' : 'Your vote:'}
                </Typography>
                {voteResponse.name && (
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#772432', mb: 1 }}>
                    {voteResponse.name}
                  </Typography>
                )}
                {voteResponse.speechTitle && (
                  <Typography variant="body1" sx={{ fontWeight: 400, color: '#666', mb: 1, fontStyle: 'italic' }}>
                    "{voteResponse.speechTitle}"
                  </Typography>
                )}
                {voteResponse.imageUrl && (
                  <CardMedia
                    component="img"
                    image={`${process.env.REACT_APP_API_URL}/api/${session}/image/${voteResponse.imageUrl.split('/').pop()}`}
                    alt={voteResponse.participantName}
                    sx={{
                      objectFit: 'cover',
                      borderRadius: 3,
                      width: 140,
                      height: 140,
                      boxShadow: 0,
                      border: '2px solid #772432',
                      background: '#fff',
                      mb: 2
                    }}
                  />
                )}
              </Box>
            )}
          </Box>
        )}
        {!voted && !hasVoted && <Box sx={{
          mt: 2,
          mx: { xs: 0, sm: 0 },
          mb: { xs: 3, sm: 3 },
          textAlign: 'center',
          position: { xs: 'fixed', sm: 'fixed' },
          left: { xs: '50%', sm: '50%' },
          transform: { xs: 'translateX(-50%)', sm: 'translateX(-50%)' },
          bottom: { xs: 0, sm:  0 },
          width: '80%',
          zIndex: 1000,
          bgcolor: '#ffffff',
          p: 2,
          borderRadius: 3,
          boxShadow: { xs: 3, sm: 3 },
          '& .MuiButton-root': {
            animation: selectedId ? 'colorNudge 2s ease-in-out infinite' : 'none'
          }
        }}>
          <Button
            variant="contained"
            color="primary"
            disabled={voted || !selectedId || hasVoted}
            onClick={() => selectedId && handleVote(selectedId)}
            sx={{
              width: { xs: '100%', sm: '100%' },
              fontSize: { xs: '0.8rem', sm: '0.8rem' },
              py: { xs: 0.8, sm: 0.8 },
              borderRadius: 3,
              boxShadow: 0,
              fontWeight: 600,
              mx: { xs: 0, sm: 0 },
              mb: { xs: 0, sm: 0 },
              transition: 'all 0.3s ease',
              transform: 'scale(1)',
              '&:hover': {
                transform: selectedId && !voted && !hasVoted ? 'scale(1.08)' : 'scale(1.02)'
              }
            }}
          >
            Submit Vote
          </Button>
        </Box>}
      </Container>
      )}
    </>
  );
};

export default VotingPage;

